import { completeRunSheetCodes, missingRunSheetParts, type RunSheetAssembly } from '@barso/shared';
import type { RunSheetHint } from '../pos/runSheetImport';
import { Button } from '../components/ui';

/** Ergebnis der Übernahme eines Laufzettels in den Warenkorb (nur Anzeige). */
export interface RunSheetResultView {
  /** Artikel laut Laufzettel */
  total: number;
  /** Davon in den Warenkorb gekommen (ohne Rückfragen) */
  added: number;
  hints: readonly RunSheetHint[];
  /** Artikel, die schon vor der Übernahme im Warenkorb lagen (die Zahl „laut Laufzettel“ bezieht sich nur auf den Korb des Laufzettels). */
  alreadyInCart?: number;
}

/** Zahl und Wort: „1 Artikel“, „12 Artikel“. */
const articles = (count: number): string => `${String(count)} Artikel`;

/**
 * Laufzettel an der Kasse (Plan Welle 3): Der QR-Code kommt von einem Helfer-Handy aus der Schlange. Die Kasse übernimmt
 * die Codes erst mit „In den Warenkorb“; die große Zeile erinnert daran, den Korb zu zählen: Der Laufzettel ist nicht
 * signiert, ein QR mit weniger Codes als im Korb läge unbezahlt auf dem Tresen.
 */
export function RunSheetCard({
  assembly,
  result,
  disabled,
  onImport,
  onDiscard,
  onDismiss,
}: {
  /** Offener Laufzettel (Teile werden noch gelesen oder warten auf die Übernahme). */
  assembly: RunSheetAssembly | null;
  /** Ergebnis der letzten Übernahme; `null` ohne. */
  result: RunSheetResultView | null;
  /** Ein Blatt oder eine Rückfrage ist offen. */
  disabled: boolean;
  onImport: () => void;
  onDiscard: () => void;
  onDismiss: () => void;
}) {
  if (assembly !== null) {
    const codes = completeRunSheetCodes(assembly);
    if (codes !== null) {
      return (
        <section className="cash__sheet" aria-label="Laufzettel">
          <p className="cash__sheet-title" role="status">
            {articles(codes.length)} laut Laufzettel – bitte Korb zählen
          </p>
          <div className="row">
            <Button disabled={disabled} onClick={onImport}>
              In den Warenkorb
            </Button>
            <Button variant="secondary" disabled={disabled} onClick={onDiscard}>
              Verwerfen
            </Button>
          </div>
        </section>
      );
    }
    const missing = missingRunSheetParts(assembly);
    const read = [...assembly.received.values()].reduce((sum, part) => sum + part.length, 0);
    return (
      <section className="cash__sheet" aria-label="Laufzettel">
        <p className="cash__sheet-title" role="status">
          Laufzettel:{' '}
          {missing.map((part) => `Teil ${String(part)}/${String(assembly.parts)}`).join(', ')} fehlt
        </p>
        <p className="muted">
          {articles(read)} bisher gelesen. Bitte den nächsten QR-Code des Laufzettels scannen.
        </p>
        <div className="row">
          <Button variant="secondary" disabled={disabled} onClick={onDiscard}>
            Verwerfen
          </Button>
        </div>
      </section>
    );
  }
  if (result === null) return null;
  return (
    <section className="cash__sheet" aria-label="Laufzettel übernommen">
      <p className="cash__sheet-title" role="status">
        {articles(result.total)} laut Laufzettel – bitte Korb zählen
      </p>
      <p>
        {articles(result.added)} im Warenkorb
        {result.hints.length > 0 && `, ${String(result.hints.length)} mit Hinweis`}
      </p>
      {(result.alreadyInCart ?? 0) > 0 && (
        <p>
          Der Warenkorb war nicht leer: {articles(result.alreadyInCart ?? 0)} lagen schon darin.
          Bitte beim Zählen nur den Korb des Laufzettels gegenrechnen.
        </p>
      )}
      {result.hints.length > 0 && (
        <ul className="cash__sheet-hints">
          {result.hints.map((hint) => (
            <li key={hint.code}>
              <strong>{hint.label}</strong>: {hint.text}
            </li>
          ))}
        </ul>
      )}
      <div className="row">
        <Button variant="secondary" onClick={onDismiss}>
          Verstanden
        </Button>
      </div>
    </section>
  );
}
